import getMailListHeader from './getMailListHeader';
import { messageListLabel, skipToMessageList } from './ListPane.locstring.json';
import MailListColumnHeaders from './MailListColumnHeaders';
import { observer } from 'owa-mobx-react';
import loc from 'owa-localize';
import { getNativeAdPlacement, type NativeAdPlacement } from 'owa-mail-ads-shared';
import { resetFocus } from 'owa-mail-focus-manager';
import {
    getStore as getListViewStore,
    getFocusedFilterForTable,
    getIsSearchTableShown,
    type MailFolderTableQuery,
    isCatchUpFilter,
    isHeadlinesFilter,
} from 'owa-mail-list-store';
import MailList from 'owa-mail-list-view/lib/components/MailList';
import { MessageAdList } from 'owa-mail-messagead-list-view';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { getNativeadsUpsellEffectStore } from 'owa-nativeadsupselleffect-store';
import { NotificationBarHost } from 'owa-notification-bar';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { SkipLinkRegion } from 'owa-skip-link/lib/components/SkipLinkRegion';
import React from 'react';
import { shouldShowPstTasks, LazyPstTasksList } from 'owa-psttasks-view';
import { isGlobalFeatureEnabled, isAccountFeatureEnabled } from 'owa-feature-flags';
import {
    getListViewDimensions,
    getDataMinMaxDimensionsForListView,
    isReadingPanePositionRight,
    shouldRenderColumnHeaders,
    getLeftNavWidth,
    shouldShowFolderPane,
} from 'owa-mail-layout';
import type { UserSettingsStyleSelector } from 'owa-mail-list-view/lib/utils/getStyleSelectorAsPerUserSettings';
import { RollupContainer } from 'owa-mail-rollup';
import onPaste from '../utils/onPaste';
import { addWrappedEventListener } from 'owa-event-listener';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list/lib/flights';
import { LazyAddAccountCampaign } from 'owa-mail-multi-account-campaigns';

export interface ListPaneProps {
    tableViewId: string;
    tabBar: React.JSX.Element | null;
    className?: string;
    shouldShowHeader: boolean;
    style?: React.CSSProperties;
    styleSelectorAsPerUserSettings: UserSettingsStyleSelector;
    isBleedThroughEnabled?: boolean;
    shouldHostNonRightNotificationBar?: boolean;
}

const ListPane: React.FunctionComponent<ListPaneProps> = observer(function ListPane(
    props: ListPaneProps
) {
    const {
        tableViewId,
        tabBar,
        className,
        shouldShowHeader,
        style,
        styleSelectorAsPerUserSettings,
    } = props;

    const tableView = getListViewStore().tableViews.get(tableViewId);
    const folderId = tableView?.tableQuery.folderId;
    const isInboxFolderSelected = folderId ? 'inbox' === folderIdToName(folderId) : false;
    const mailboxInfo = getModuleContextMailboxInfo();
    const { dataWidthMin, dataHeightMin, dataWidthMax, dataHeightMax } =
        getDataMinMaxDimensionsForListView();
    const isSearchTableShown = getIsSearchTableShown();
    const mailListSkipLinkRegionRef = React.useRef<HTMLDivElement | null>(null);
    const removeKeyDownListenerRef = React.useRef<(() => void) | undefined>();

    const isPstTasksEnabled = isGlobalFeatureEnabled('mon-pst-task') && shouldShowPstTasks();

    const setMailListSkipLinkRegionRef = React.useCallback((ref: HTMLDivElement) => {
        if (ref) {
            mailListSkipLinkRegionRef.current = ref;
            removeKeyDownListenerRef.current = addWrappedEventListener(
                'ListPane',
                mailListSkipLinkRegionRef.current,
                'keydown',
                onPaste
            );
        } else {
            if (mailListSkipLinkRegionRef.current) {
                removeKeyDownListenerRef.current?.();
                removeKeyDownListenerRef.current = undefined;
                mailListSkipLinkRegionRef.current = null;
            }
        }
    }, []);

    const isConsumerAccount = isConsumer(undefined, mailboxInfo);

    const showAddAccountCampaign =
        isInboxFolderSelected &&
        !isSearchTableShown &&
        isMonarchMultipleAccountsEnabled() &&
        isAccountFeatureEnabled('mon-acct-mailInboxCampaign', mailboxInfo);

    return (
        <SkipLinkRegion
            skipLinkName={loc(skipToMessageList)}
            role={'complementary'}
            aria-label={loc(messageListLabel)}
            tabIndex={-1}
            style={style}
            className={className}
            data-min-width={dataWidthMin}
            data-min-height={dataHeightMin}
            data-max-width={dataWidthMax}
            data-max-height={dataHeightMax}
            ref={setMailListSkipLinkRegionRef}
        >
            {isConsumerAccount &&
                (getNativeAdPlacement() == 1 || getNativeAdPlacement() == 2) &&
                !getNativeadsUpsellEffectStore().isEffective &&
                tableView && (
                    <MessageAdList
                        loadInFocusedPivot={false}
                        loadInOtherPivot={false}
                        loadInEmptyPivot={false}
                        loadInItemView={tableView?.tableQuery?.listViewType == 1}
                        loadOnHeader={true}
                        requestingAnotherFloatingAd={false}
                        middleFloatingAdOnly={false}
                        shouldShowTwoAds={false}
                        tableView={tableView}
                        adIndex={0}
                    />
                )}
            {/* Don't show mail list header when PST tasks are being displayed */}
            {shouldShowHeader &&
                !isPstTasksEnabled &&
                getMailListHeader(tableViewId, styleSelectorAsPerUserSettings)}
            {/* Don't show mail-specific UI components when PST tasks are displayed */}
            {!isCatchUpFilter(tableView?.tableQuery as MailFolderTableQuery) &&
                !isHeadlinesFilter(tableView?.tableQuery as MailFolderTableQuery) &&
                shouldRenderColumnHeaders(tableViewId) &&
                folderId &&
                !isPstTasksEnabled && (
                    <RollupContainer
                        key="rollup_0"
                        focusedViewFilter={getFocusedFilterForTable(tableView)}
                        tableView={tableView}
                        shouldShowRollupOverride={true}
                    />
                )}
            {showAddAccountCampaign && <LazyAddAccountCampaign mailboxInfo={mailboxInfo} />}
            {shouldRenderColumnHeaders(tableViewId) && folderId && !isPstTasksEnabled && (
                <MailListColumnHeaders tableViewId={tableViewId} folderId={folderId} />
            )}
            {isPstTasksEnabled ? (
                <LazyPstTasksList mailboxInfo={mailboxInfo} />
            ) : (
                <MailList
                    key="listpane_list"
                    tableViewId={tableViewId}
                    styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                    isBleedThroughEnabled={props.isBleedThroughEnabled}
                />
            )}
            {isConsumerAccount &&
                getNativeAdPlacement() == 3 &&
                !getNativeadsUpsellEffectStore().isEffective &&
                tableView && (
                    <MessageAdList
                        loadInFocusedPivot={false}
                        loadInOtherPivot={false}
                        loadInEmptyPivot={false}
                        loadInItemView={tableView?.tableQuery?.listViewType == 1}
                        loadOnHeader={true}
                        requestingAnotherFloatingAd={false}
                        middleFloatingAdOnly={false}
                        shouldShowTwoAds={false}
                        tableView={tableView}
                        adIndex={0}
                    />
                )}
            {(isReadingPanePositionRight() || props.shouldHostNonRightNotificationBar) && (
                <NotificationBarHost
                    hostId={'MailModuleNotificationBarHost'}
                    onNotificationBlur={onNotificationBlurCallback}
                    positionOffset={isReadingPanePositionRight() ? 'bottom-start' : 'bottom'}
                    leftNavWidth={getLeftNavWidth()}
                    listViewWidth={getListViewDimensions().listViewWidth}
                    isFolderPaneExpanded={shouldShowFolderPane()}
                    horizontalOffset={isReadingPanePositionRight() ? 50 : 0}
                    verticalOffset={16}
                />
            )}
            {tabBar}
        </SkipLinkRegion>
    );
},
'ListPane');
export default ListPane;

const onNotificationBlurCallback = () => {
    resetFocus('ListPaneNotificationBlur');
};
